import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

export interface ExportField {
    label: string;
    value: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    title?: string;
    fields: ExportField[];
    loading?: boolean;
    onExport: (selected: ExportField[]) => void | Promise<void>;
}

const IconCheck = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

export const ExportFieldsModal: React.FC<Props> = ({
    open,
    onClose,
    title = "Select fields to export",
    fields,
    loading = false,
    onExport,
}) => {
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!open) setSelected(new Set());
    }, [open]);

    const sortedFields = useMemo(
        () => [...fields].sort((a, b) => a.label.localeCompare(b.label)),
        [fields],
    );

    const toggle = (value: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });

    const allOn = selected.size === sortedFields.length && sortedFields.length > 0;
    const toggleAll = () =>
        setSelected(allOn ? new Set() : new Set(sortedFields.map((f) => f.value)));

    const handleExport = () => {
        if (selected.size === 0 || loading) return;
        const chosen = sortedFields.filter((f) => selected.has(f.value));
        onExport(chosen);
    };

    const close = () => {
        if (loading) return;
        onClose();
    };

    const Checkbox = ({ on }: { on: boolean }) => (
        <div
            className={`w-[18px] h-[18px] rounded-[4px] border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all ${
                on ? "bg-accent border-accent" : "border-[#D5D0C8] bg-transparent"
            }`}
        >
            {on && <IconCheck />}
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={close}
            PaperProps={{
                sx: {
                    width: 480,
                    maxWidth: "calc(100% - 24px)",
                    m: { xs: 1.5, sm: 4 },
                    borderRadius: "14px",
                    fontFamily: "inherit",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.1)",
                },
            }}
        >
            <div className="flex flex-col max-h-[calc(100vh-48px)]">
                <div className="flex items-center justify-between px-4 sm:px-7 pt-4 sm:pt-6 pb-3 sm:pb-4">
                    <h2 className="text-[16px] sm:text-[18px] font-bold text-accent-text">{title}</h2>
                    <button
                        type="button"
                        onClick={close}
                        className="w-7 h-7 rounded-full bg-surface-2 hover:bg-track text-muted hover:text-text-1 text-[14px] flex items-center justify-center transition-all"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="flex items-center justify-between px-4 sm:px-7 pb-3 sm:pb-4">
                    <button
                        type="button"
                        onClick={toggleAll}
                        className="flex items-center gap-3 text-[13px] font-semibold text-text-1 bg-transparent border-none cursor-pointer"
                    >
                        <Checkbox on={allOn} />
                        {allOn ? "Deselect all" : "Select all"}
                    </button>
                    {selected.size > 0 && (
                        <span className="text-[12px] font-semibold text-accent-text">
                            {selected.size} selected
                        </span>
                    )}
                </div>

                <div className="h-px bg-border-token mx-4 sm:mx-7" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 px-3 sm:px-5 py-3 sm:py-4 overflow-y-auto max-h-[380px]">
                    {sortedFields.map((f) => {
                        const on = selected.has(f.value);
                        return (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => toggle(f.value)}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-md border-none bg-transparent hover:bg-surface-2 text-[13.5px] cursor-pointer transition-colors text-left ${
                                    on ? "text-text-1 font-medium" : "text-text-2"
                                }`}
                            >
                                <Checkbox on={on} />
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                <div className="h-px bg-border-token mx-4 sm:mx-7" />

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-7 py-4 sm:py-5">
                    <button
                        type="button"
                        onClick={close}
                        disabled={loading}
                        className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-border-token bg-surface text-[13px] font-medium text-text-2 hover:border-text-1 hover:text-text-1 cursor-pointer disabled:opacity-60 disabled:cursor-default"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={selected.size === 0 || loading}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg border-none text-[13px] font-semibold text-white transition-all ${
                            selected.size === 0 || loading
                                ? "bg-track text-muted cursor-not-allowed"
                                : "bg-accent hover:bg-[#8A6A37] cursor-pointer"
                        }`}
                    >
                        Export{selected.size > 0 ? ` (${selected.size})` : ""}
                        {loading && <CircularProgress color="inherit" size={14} />}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default ExportFieldsModal;
